import { type CrudFilter, useAutocomplete } from 'admin';
import type { FullMaterialSupportedComponent, MaterialComponentCategory } from 'api';

import { AutocompleteField, type AutocompleteFieldProps } from '../AutocompleteField/AutocompleteField';

type Props = Omit<AutocompleteFieldProps<FullMaterialSupportedComponent>, 'options' | 'defaultValue'> & {
  materialId?: number;
  materialColourId?: number;
  category?: MaterialComponentCategory;
  defaultValue?: FullMaterialSupportedComponent['id'];
  'data-testid'?: string;
};

const getOptionLabel = (option: FullMaterialSupportedComponent) => {
  if (!option || !option.component) {
    return '';
  }
  return option.component.name;
};

export const MaterialSupportedComponentAutocomplete = ({
  materialId,
  materialColourId,
  category,
  defaultValue,
  'data-testid': dataTestId = 'material-component-autocomplete',
  ...rest
}: Props) => {
  const categoryFilter: CrudFilter[] = category
    ? [{ field: 'material_component_category', operator: 'eq', value: category }]
    : [];
  const { autocompleteProps } = useAutocomplete<FullMaterialSupportedComponent>({
    defaultValue: defaultValue,
    defaultValueQueryOptions: {
      // the current endpoint doesn't support filtering by id, so we don't use it
      enabled: false,
    },
    filters: [
      {
        field: 'material_colour_id',
        operator: 'eq',
        value: materialColourId,
      },
      ...categoryFilter,
    ],
    onSearch: (value) => [
      {
        field: 'search',
        operator: 'contains',
        value,
      },
    ],
    queryOptions: {
      enabled: Boolean(materialId),
    },
    resource: `/materials/v1/materials/${materialId}/supported-components/`,
  });

  return (
    <AutocompleteField<FullMaterialSupportedComponent>
      {...autocompleteProps}
      {...rest}
      data-testid={dataTestId}
      getOptionLabel={(item) => getOptionLabel(item)}
      valueField="id"
    />
  );
};
